/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React from 'react';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { TUnifiedCollectionPreview } from '../../../core/types';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import { DateTime } from '../../../core/utils';
import {
    Badge,
    ContentDescription,
    ContentTitle,
    Credits,
    Genres,
    ParentalControlRating,
    Separator,
    ShowcaseBanner,
} from '../ui-kit';
import type { TInfoCardContentUtil } from './InfoCardProps';

export const getSeriesInfoCardContent: TInfoCardContentUtil = (metadata) => {
    const oPreviewAssets = new PreviewComponentAssets();
    const {
        title = '',
        description = oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        ),
        unifiedCollectionId = '',
        internalRatingList = [],
        channel,
        creditString = '',
        categoryLabel = '',
        premierYear,
        startTime,
        endTime,
    } = metadata as TUnifiedCollectionPreview;

    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });

    const pcRatingNames = PreviewComponent.getParentalRatingNames(internalRatingList || []);
    const url: string = assetService.getShowcaseBannerImageUrlForVodContent(unifiedCollectionId);
    const firstAiredYear = premierYear || new DateTime(startTime).strftime('%Y');
    const lastAiredYear = new DateTime(endTime).strftime('%Y');
    const isFirstLastAiredYearSame = firstAiredYear === lastAiredYear;

    const getBodyContent = (): JSX.Element => {
        return (
            <React.Fragment>
                <ContentTitle title={title} />

                <div className="uikit line-height-1">
                    <Badge text={channel?.videoResolution} uppercase={true} />
                    <Badge text={'New'} />
                    <ParentalControlRating ratingNames={pcRatingNames} />
                    <Separator />
                    <Genres categoryLabel={categoryLabel} />
                    <Separator />
                    <span className="uikit primary-text-style muted-color text-light">
                        {isFirstLastAiredYearSame
                            ? `${firstAiredYear}`
                            : `${firstAiredYear} - ${lastAiredYear}`}
                    </span>
                </div>

                <Credits creditString={creditString} />
                <ContentDescription description={description} />
            </React.Fragment>
        );
    };

    return {
        headerContent: <ShowcaseBanner bannerUrl={url} fallbackText={title} />,
        headerClassName: 'banner-only',
        bodyContent: getBodyContent(),
    };
};
